// src/services/chatSend.js
//
// Chat v2 send-message helper. Writes a new doc to
// chats/{chatId}/messages/{auto} with serverTimestamp. The Cloud
// Function `onChatMessageCreated_v2` (functions/modules/chatChannels.js)
// then denormalizes lastMessage onto the parent chat doc and pushes
// notifications to other participants.
//
// Firestore rules from Phase 1 enforce:
//   - senderId must equal auth.uid (no impersonation)
//   - DM chats: any participant can post
//   - team_announcement: only members with role='admin' can post
//   - text max 5000 chars
//
// Phase 5 will add image attachments via a separate `sendChatImage`
// helper that uploads to Storage first, then writes the message
// with attachment metadata.

import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

/**
 * Send a text message to a chat.
 *
 * @param {Object} params
 * @param {string} params.chatId
 * @param {string} params.senderId  Firebase Auth uid
 * @param {string} params.senderName Denormalized display name
 * @param {string|null} params.senderPhotoURL Denormalized photo
 * @param {string} params.text
 * @returns {Promise<string>} the new message id
 */
export async function sendChatMessage({
  chatId,
  senderId,
  senderName,
  senderPhotoURL = null,
  text,
}) {
  if (!chatId) throw new Error('chatId required')
  if (!senderId) throw new Error('senderId required')

  const trimmed = String(text || '').trim()
  if (!trimmed) throw new Error('Message is empty')
  if (trimmed.length > 5000) throw new Error('Message too long (max 5000 characters)')

  const ref = await addDoc(
    collection(db, 'chats', chatId, 'messages'),
    {
      senderId,
      senderName: senderName || 'Member',
      senderPhotoURL: senderPhotoURL || null,
      type: 'text',
      text: trimmed,
      attachment: null,
      sentAt: serverTimestamp(),
      edited: false,
      editedAt: null,
      deletedAt: null,
    }
  )
  return ref.id
}
